import i18n from '../../i18n';
import { automationsApi, tagsApi, type FlowTemplate, type GraphWrite, type ScheduledPost } from '../../lib/api';
import {
  askTextProblems,
  dmProblems,
  keywordProblems,
  MAX_BUTTON_LABEL,
  MAX_DM_LENGTH,
  publicReplyProblems,
  type LinkButton,
  type MatchMode,
} from '../automations/quick/recipe';

/**
 * Automations set up with a post (D-103), edited on the post itself and
 * switched on with it. Every one answers comments on this post only: its
 * comment trigger waits for the post (`scheduledPostId`) and starts matching
 * when ChatFlow publishes it.
 *
 * A **comment flow** is one form that covers what the comment templates do —
 * which comments count, a public reply, followers only (with a message for
 * everyone else), the DM with a link, a tag. The templates are presets of it,
 * so nothing has to be finished in the builder. Anything beyond the form
 * (waits, HTTP calls, branching on tags) is built in the builder, which can
 * pin a scheduled post too. A flow can be saved as a workspace template
 * (D-104) and reused on the next posts, still editable on each.
 */

export type FlowPreset = 'auto_dm' | 'reply' | 'giveaway' | 'follow_gate' | 'dm_tag' | 'custom';

export interface CommentFlow {
  /** Local key for the list; never sent. */
  key: string;
  preset: FlowPreset;
  /** Set when the flow comes from a saved template: the card's title. */
  name?: string;
  match: MatchMode;
  keywords: string[];
  publicReply: { enabled: boolean; text: string };
  /** Followers get the DM; everyone else gets `askText`. */
  followGate: { enabled: boolean; askText: string };
  /**
   * With `followGate` on, the DM the flow opens with (D-118). Instagram says
   * whether someone follows you only once they have written to you, so the
   * check waits for a tap on this message's button.
   */
  opener: { text: string; buttonLabel: string };
  dm: { enabled: boolean; text: string; button: LinkButton | null };
  /** Added to whoever gets the DM (or to every commenter when there is no DM). */
  tag: { enabled: boolean; name: string };
}

/** Every preset; the label and description are in the catalogue (`postAutomations.presets`). */
export const FLOW_PRESETS: Record<FlowPreset, true> = {
  auto_dm: true,
  reply: true,
  giveaway: true,
  follow_gate: true,
  dm_tag: true,
  custom: true,
};

export const presetLabel = (preset: FlowPreset): string => i18n.t(`postAutomations.presets.${preset}.label`);
export const presetDescription = (preset: FlowPreset): string => i18n.t(`postAutomations.presets.${preset}.description`);

/** Presets offered in the picker, in order; `custom` is offered separately. */
export const READY_MADE: readonly FlowPreset[] = ['auto_dm', 'reply', 'giveaway', 'follow_gate', 'dm_tag'];

let keySeq = 0;

/** The opening DM of a follower-only flow, per preset (D-118); same copy as the matching template. */
function defaultOpener(preset: FlowPreset): CommentFlow['opener'] {
  if (preset === 'giveaway') return { text: 'Want to enter the giveaway, {{first_name|there}}? Tap below 👇', buttonLabel: 'Enter the giveaway' };
  if (preset === 'follow_gate') {
    return { text: "Hey {{first_name|there}} 👋 Thanks for your comment! Tap below and I'll send you the link.", buttonLabel: 'Send me the link' };
  }
  return { text: "Hey {{first_name|there}} 👋 Thanks for your comment! Tap below and it's yours.", buttonLabel: 'Send it to me' };
}

/** The button a non-follower taps once they follow, to be checked again (D-118). */
export const FOLLOWING_BUTTON_LABEL = "I'm following";

/** A flow from a preset, with the same copy as the matching template. */
export function presetFlow(preset: FlowPreset): CommentFlow {
  keySeq += 1;
  const base: CommentFlow = {
    key: `flow-${keySeq}`,
    preset,
    match: 'any_word',
    keywords: [],
    publicReply: { enabled: false, text: 'Sent you a DM {{first_name|there}} 💌' },
    followGate: { enabled: false, askText: 'Follow us, then tap below and it is yours! ✨' },
    opener: defaultOpener(preset),
    dm: { enabled: false, text: '', button: null },
    tag: { enabled: false, name: '' },
  };
  switch (preset) {
    case 'auto_dm':
      return { ...base, dm: { enabled: true, text: "Hey {{first_name|there}} 👋 Thanks so much for commenting — here's the link ✨", button: null } };
    case 'reply':
      return { ...base, publicReply: { enabled: true, text: 'Thanks {{first_name|there}}! 🙌 We will get back to you shortly.' } };
    case 'giveaway':
      return {
        ...base,
        publicReply: { enabled: true, text: 'Check your DMs 👀' },
        followGate: { enabled: true, askText: 'Almost there! Follow us, then tap below to enter the giveaway. 💜' },
        dm: { enabled: true, text: "You're in, {{first_name|there}}! 🎉 We'll announce the winner on Friday — good luck!", button: null },
        tag: { enabled: true, name: 'Giveaway entrant' },
      };
    case 'follow_gate':
      return {
        ...base,
        followGate: { enabled: true, askText: 'I share this link with followers only. Follow me, then tap below and it is yours! ✨' },
        dm: { enabled: true, text: 'Thanks for following, {{first_name|there}}! Here is the link you asked for:', button: null },
      };
    case 'dm_tag':
      return {
        ...base,
        dm: {
          enabled: true,
          text: 'Hey {{first_name|there}} 👋 Thanks for your comment! Here is the info you asked about — reply here if you have any questions.',
          button: null,
        },
        tag: { enabled: true, name: 'Commented' },
      };
    case 'custom':
      return { ...base, dm: { enabled: true, text: '', button: null } };
  }
}

/** The card's title: the template's name, or the preset's. */
export const flowLabel = (flow: CommentFlow): string => flow.name ?? presetLabel(flow.preset);

/** A flow as a template stores it: no list key, no title. */
export function templateFlowOf(flow: CommentFlow): FlowTemplate['flow'] {
  const { key: _key, name: _name, ...rest } = flow;
  return rest;
}

/** A flow kept from an earlier visit, back as a card with a key of this page's (D-132). */
export function rekeyedFlow(flow: Omit<CommentFlow, 'key'>): CommentFlow {
  keySeq += 1;
  return { ...flow, key: `flow-${keySeq}` };
}

/** A card from a saved template, titled with its name. */
export function flowFromTemplate(template: FlowTemplate): CommentFlow {
  keySeq += 1;
  // Templates saved before D-118 have no opener: they get the preset's.
  const opener = template.flow.opener ?? defaultOpener(template.flow.preset);
  return { ...template.flow, opener, key: `flow-${keySeq}`, name: template.name };
}

const MAX_TAG_NAME = 50;

export function flowProblems(flow: CommentFlow): string[] {
  const problems: string[] = keywordProblems(flow.match, flow.keywords, 'comment');
  if (!flow.publicReply.enabled && !flow.dm.enabled) problems.push(i18n.t('postAutomations.problems.replyOrDm'));
  problems.push(...publicReplyProblems(flow.publicReply));
  if (flow.followGate.enabled && !flow.dm.enabled) problems.push(i18n.t('postAutomations.problems.followersNeedDm'));
  problems.push(...askTextProblems(flow.followGate));
  problems.push(...openerProblems(flow));
  if (flow.dm.enabled) problems.push(...dmProblems(flow.dm));
  if (flow.tag.enabled && !flow.tag.name.trim()) problems.push(i18n.t('postAutomations.problems.tagName'));
  if (flow.tag.name.trim().length > MAX_TAG_NAME) problems.push(i18n.t('postAutomations.problems.tagLength', { max: MAX_TAG_NAME }));
  return problems;
}

/** What is wrong with the opening DM a follower-only flow sends first. */
function openerProblems(flow: CommentFlow): string[] {
  if (!flow.followGate.enabled) return [];
  const problems: string[] = [];
  if (!flow.opener.text.trim()) problems.push(i18n.t('postAutomations.problems.opener'));
  if (flow.opener.text.length > MAX_DM_LENGTH) problems.push(i18n.t('postAutomations.problems.openerLength', { max: MAX_DM_LENGTH }));
  if (!flow.opener.buttonLabel.trim()) problems.push(i18n.t('postAutomations.problems.buttonLabel'));
  if (flow.opener.buttonLabel.length > MAX_BUTTON_LABEL) problems.push(i18n.t('postAutomations.problems.buttonLength', { max: MAX_BUTTON_LABEL }));
  return problems;
}

export interface PostAutomationsDraft {
  flows: CommentFlow[];
}

export function emptyPostAutomations(): PostAutomationsDraft {
  return { flows: [] };
}

/** What still blocks saving, phrased for the composer's list of problems. */
export function postAutomationProblems(draft: PostAutomationsDraft): string[] {
  return draft.flows.flatMap((flow) => flowProblems(flow).map((problem) => i18n.t('postAutomations.problems.prefixed', { name: flowLabel(flow), problem })));
}

const NODE_GAP = 160;
const BRANCH_X = 180;

/**
 * The flow as a graph: trigger → [words] → [public reply] → either
 * opening DM —tap→ follows? (yes: DM → [tag], no: ask to follow —tap→ follows?)
 * or DM → [tag], or just the tag.
 */
export function compileFlow(flow: CommentFlow, scheduledPostId: string, channelCode: string | null, tagId: string | null): GraphWrite {
  const nodes: GraphWrite['nodes'] = [];
  const edges: GraphWrite['edges'] = [];
  let previous: { key: string; branch: string } | null = null;
  let x = 0;
  const add = (key: string, type: string, config: Record<string, unknown>, from = previous, outBranch = 'default') => {
    if (from) edges.push({ fromKey: from.key, toKey: key, branch: from.branch });
    // A single column; after the follow check the two answers sit side by side.
    const depth = x === 0 ? nodes.length : nodes.filter((n) => n.position.x === 0).length + nodes.filter((n) => n.position.x === x).length;
    nodes.push({ key, type, config, position: { x, y: depth * NODE_GAP } });
    previous = { key, branch: outBranch };
  };

  add('trigger', 'comment_received', { ...(channelCode ? { channels: [channelCode] } : {}), scheduledPostId });
  if (flow.match === 'specific_words' && flow.keywords.length > 0) {
    add('keyword', 'message_contains_keyword', { keywords: flow.keywords }, previous, 'true');
  }
  if (flow.publicReply.enabled) add('public-reply', 'reply_comment', { text: flow.publicReply.text.trim() });

  const dmConfig = () => {
    const button = flow.dm.button;
    const hasButton = button !== null && button.label.trim() !== '' && button.url.trim() !== '';
    return {
      text: flow.dm.text.trim(),
      ...(hasButton ? { buttons: [{ id: 'open-link', label: button.label.trim(), url: button.url.trim() }] } : {}),
    };
  };

  if (flow.followGate.enabled && flow.dm.enabled) {
    // Instagram says whether a commenter follows you only once they have
    // written to you (D-118): open with a button and check on the tap.
    // Non-followers get an "I'm following" button that checks again.
    add('opening-dm', 'send_message', {
      text: flow.opener.text.trim(),
      buttons: [{ id: 'tap', label: flow.opener.buttonLabel.trim() }],
    });
    add('check-follow', 'follows_account', {}, { key: 'opening-dm', branch: 'button:tap' });
    const check = previous!;
    x = -BRANCH_X;
    add('dm', 'send_message', dmConfig(), { key: check.key, branch: 'true' });
    if (flow.tag.enabled && tagId) add('tag', 'add_tag', { tagId });
    x = BRANCH_X;
    add(
      'ask-follow',
      'send_message',
      { text: flow.followGate.askText.trim(), buttons: [{ id: 'following', label: FOLLOWING_BUTTON_LABEL }] },
      { key: check.key, branch: 'false' },
    );
    edges.push({ fromKey: 'ask-follow', toKey: check.key, branch: 'button:following' });
  } else {
    if (flow.dm.enabled) add('dm', 'send_message', dmConfig());
    if (flow.tag.enabled && tagId) add('tag', 'add_tag', { tagId });
  }
  return { nodes, edges };
}

export function flowName(flow: CommentFlow, post: ScheduledPost): string {
  const caption = post.caption.trim().replace(/\s+/g, ' ');
  const short = caption.length > 30 ? `${caption.slice(0, 30)}…` : caption;
  return short ? `${flowLabel(flow)} · ${short}` : flowLabel(flow);
}

export interface PostAutomationOutcome {
  name: string;
  /** null when nothing was created. */
  id: string | null;
  /** `on`: switched on, goes live with the post. `not_on`: created but not switched on. `failed`: not created. */
  status: 'on' | 'not_on' | 'failed';
  reason: string | null;
}

const reasonOf = (err: unknown) => (err instanceof Error ? err.message : i18n.t('postAutomations.outcome.unknown'));

/** Finds the workspace's tag by name (any case) or creates it. */
async function tagIdFor(name: string, cache: Map<string, string>): Promise<string> {
  const wanted = name.trim();
  const known = cache.get(wanted.toLowerCase());
  if (known) return known;
  const created = await tagsApi.create({ name: wanted });
  cache.set(wanted.toLowerCase(), created.id);
  return created.id;
}

async function createFlow(
  flow: CommentFlow,
  post: ScheduledPost,
  tags: Map<string, string>,
  onCreated: ((flow: CommentFlow) => void) | undefined,
): Promise<PostAutomationOutcome> {
  const name = flowName(flow, post);
  let id: string | null = null;
  let filled = false;
  try {
    const tagId = flow.tag.enabled ? await tagIdFor(flow.tag.name, tags) : null;
    const created = await automationsApi.create({
      name,
      description: 'Set up with a scheduled post: answers comments once the post is published.',
      templateKey: 'post:comment_flow',
    });
    id = created.id;
    // It exists from here on, switched on or not: never to be created again.
    onCreated?.(flow);
    const saved = await automationsApi.saveGraph(id, compileFlow(flow, post.id, post.channelAccount.channelCode, tagId));
    filled = true;
    if (saved.issues.length > 0) {
      return { name, id, status: 'not_on', reason: i18n.t('postAutomations.outcome.issues', { count: saved.issues.length }) };
    }
    await automationsApi.activate(id);
    return { name, id, status: 'on', reason: null };
  } catch (err) {
    if (id && !filled) {
      // Created but empty: worth nothing in the builder. Drop it and let the
      // next save make the flow whole; if even that fails, say what it is.
      const dropped = await automationsApi.remove(id).then(
        () => true,
        () => false,
      );
      if (dropped) return { name, id: null, status: 'failed', reason: reasonOf(err) };
      return { name, id, status: 'not_on', reason: i18n.t('postAutomations.outcome.empty', { reason: reasonOf(err) }) };
    }
    return { name, id, status: id ? 'not_on' : 'failed', reason: reasonOf(err) };
  }
}


/**
 * Creates everything for a saved post, one at a time (plan limits are counted
 * as they go). It never throws: the post is saved by then, so a failure is
 * reported next to the success, not instead of it. `onCreated` hears of each
 * automation as soon as it exists, so a save cut short (a closed tab) does not
 * leave it among the flows still to create (D-132).
 */
export async function createPostAutomations(
  draft: PostAutomationsDraft,
  post: ScheduledPost,
  onCreated?: (flow: CommentFlow) => void,
): Promise<PostAutomationOutcome[]> {
  const outcomes: PostAutomationOutcome[] = [];
  const tags = new Map<string, string>();
  if (draft.flows.some((flow) => flow.tag.enabled)) {
    try {
      for (const tag of await tagsApi.list()) tags.set(tag.name.toLowerCase(), tag.id);
    } catch {
      // Unknown tags are then created by name; a duplicate name is refused by the API and reported on that flow.
    }
  }
  for (const flow of draft.flows) outcomes.push(await createFlow(flow, post, tags, onCreated));
  return outcomes;
}

/** The automations' line in the save toast. */
export function outcomeSummary(outcomes: PostAutomationOutcome[], isPublishingNow: boolean): string | undefined {
  if (outcomes.length === 0) return undefined;
  const parts: string[] = [];
  const on = outcomes.filter((o) => o.status === 'on').length;
  if (on > 0) parts.push(i18n.t(isPublishingNow ? 'postAutomations.outcome.onNow' : 'postAutomations.outcome.onLater', { count: on }));
  for (const o of outcomes) {
    if (o.status === 'not_on') parts.push(i18n.t('postAutomations.outcome.notOn', { name: o.name, reason: o.reason }));
    if (o.status === 'failed') parts.push(i18n.t('postAutomations.outcome.failed', { name: o.name, reason: o.reason }));
  }
  if (outcomes.some((o) => o.status === 'failed')) parts.push(i18n.t('postAutomations.outcome.retry'));
  return parts.join(' ');
}
